/**
 * NoteHistoryModal - every version of one note (docs/plans/crm-report-builder-search-collab-plan.md A1.3
 * steps 2 and 5, A1.5, A5 N4 a, B1.2 getNoteHistory, B1.4; WP N-7). The original first (who wrote it,
 * when, the text as first written), then each change with who, when, why and what changed: an edit as
 * the version-history window's diff (components/versions/VersionDiffView, the changed words
 * highlighted), a delete as the text it removed.
 *
 * GET /contacts/:id/notes/:noteId/history is read fresh on every open. For a deleted note the words
 * come back only to a CRM admin, after the server has written its audit row (each open is one audited
 * view; a failed audit write is a 503 and nothing is shown); anyone else gets words_hidden and sees
 * who, when and why only. historyRows (noteLogic.ts) puts the rows in order and blanks hidden words.
 *
 * The shared Modal (overlay press and Escape close, body scroll locked) portals to <body> with
 * font-inter re-applied. Clicks and keys inside never bubble to the React tree around the caller;
 * Escape is handled in the bubble phase and stopped there. The keyboard lands in the window and goes
 * back where it was on close.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode, SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import Modal from '../components/forms/Modal';
import VersionDiffView from '../components/versions/VersionDiffView';
import { Alert, btnGhost } from '../components/ui';
import { getNoteHistory, noteErrorOf } from './api';
import { historyRows } from './noteLogic';
import type { NoteHistoryRow, NoteWordsOptions } from './noteLogic';
import type { NoteError, NoteHistoryResponse } from './types';

export interface NoteHistoryModalProps {
  open: boolean;
  onClose: () => void;
  contactId: string;
  noteId: string;
  /** A user id to a name (useTeam().byId); the names the server sent win. */
  nameOf?: NoteWordsOptions['nameOf'];
  /** The window's title; "Note history" by default (DeletedNoteCard passes "Deleted note"). */
  title?: string;
}

const stopClick = (e: SyntheticEvent) => e.stopPropagation();

export default function NoteHistoryModal(props: NoteHistoryModalProps) {
  if (!props.open) return null;
  return <HistoryWindow {...props} />;
}

function HistoryWindow({ onClose, contactId, noteId, nameOf, title = 'Note history' }: NoteHistoryModalProps) {
  const [data, setData] = useState<NoteHistoryResponse | null>(null);
  const [error, setError] = useState<NoteError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    getNoteHistory(contactId, noteId)
      .then((res) => {
        if (live) setData(res);
      })
      .catch((e) => {
        if (live) setError(noteErrorOf(e));
      });
    return () => {
      live = false;
    };
  }, [contactId, noteId, reloadKey]);

  // The keyboard lands in the window, and goes back where it was when the window closes.
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    bodyRef.current?.focus();
    return () => {
      if (before && before.isConnected) before.focus();
    };
  }, []);

  const rows = useMemo(() => historyRows(data, { nameOf }), [data, nameOf]);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  let content: ReactNode;
  if (error) {
    content = (
      <Alert
        tone="error"
        title="The history could not be loaded"
        actions={
          <button type="button" onClick={() => setReloadKey((k) => k + 1)} className={btnGhost}>
            Retry
          </button>
        }
      >
        {error.message}
      </Alert>
    );
  } else if (!data) {
    content = (
      <div role="status" className="flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <span className="sr-only">Loading the history</span>
      </div>
    );
  } else {
    const onlyOriginal = rows.length === 1 && rows[0].kind === 'original';
    content = (
      <div className="space-y-3">
        {data.words_hidden && (
          <Alert tone="info" size="sm">
            This note was deleted. Only CRM admins can read its words; who changed it, when and why are below.
          </Alert>
        )}
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No history was found for this note.</p>
        ) : (
          <ol aria-label="Versions, oldest first" className="space-y-3">
            {rows.map((row) => (
              <HistoryRow key={row.key} row={row} />
            ))}
          </ol>
        )}
        {onlyOriginal && <p className="text-xs text-gray-500 dark:text-gray-400">This note has not been changed.</p>}
      </div>
    );
  }

  return createPortal(
    <div className="font-inter" onClick={stopClick} onKeyDown={onKeyDown}>
      <Modal isOpen onClose={onClose} title={title} maxWidth="max-w-3xl">
        <div ref={bodyRef} tabIndex={-1} data-note-history={noteId} className="focus:outline-none">
          {content}
        </div>
      </Modal>
    </div>,
    document.body,
  );
}

const KIND_LABEL: Record<NoteHistoryRow['kind'], string> = {
  original: 'Original',
  edited: 'Edited',
  deleted: 'Deleted',
};

const KIND_VERB: Record<NoteHistoryRow['kind'], string> = {
  original: 'Written by',
  edited: 'Edited by',
  deleted: 'Deleted by',
};

function TextBlock({ text }: { text: string | null }) {
  return (
    <p className="whitespace-pre-wrap break-words rounded-md bg-gray-50 px-2.5 py-2 text-sm text-gray-800 dark:bg-gray-900/40 dark:text-gray-200">
      {text || '(no text)'}
    </p>
  );
}

function HiddenWords() {
  return <p className="text-xs italic text-gray-500 dark:text-gray-400">Words hidden (CRM admins only)</p>;
}

/** One version: who, when and why, then the text (the original), the diff (an edit) or the removed text (a delete). */
function HistoryRow({ row }: { row: NoteHistoryRow }) {
  let body: ReactNode;
  if (row.hidden) {
    body = <HiddenWords />;
  } else if (row.kind === 'original') {
    body = <TextBlock text={row.after} />;
  } else if (row.kind === 'edited') {
    body = (
      <VersionDiffView
        oldText={row.before ?? ''}
        newText={row.after ?? ''}
        emptyMessage="The text did not change."
      />
    );
  } else {
    body = (
      <>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Text when deleted
        </p>
        <TextBlock text={row.before} />
      </>
    );
  }

  return (
    <li data-history-row={row.kind} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <p className="min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100">
          <span className="mr-2 text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            {KIND_LABEL[row.kind]}
          </span>
          {KIND_VERB[row.kind]} {row.by}
          {row.as_admin ? ' (as a CRM admin)' : ''}
        </p>
        {row.when && <p className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">{row.when}</p>}
      </div>
      {row.reason && <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">Why: {row.reason}</p>}
      <div className="mt-2">{body}</div>
    </li>
  );
}
